'use client';

import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Trash, 
  ArrowsClockwise, 
  Calendar,
  TextT,
  CurrencyDollar,
  Swap,
  EyeSlash,
  Textbox,
  Scissors,
  Check,
  Eye,
  Play,
  NumberSquareOne,
  SplitHorizontal,
  CopySimple,
  ArrowsLeftRight,
  ArrowsInLineVertical,
  CaretDown,
  CaretRight,
} from '@phosphor-icons/react';
import { useWorkflowStore } from '../../../stores/workflowStore';
import { getNodeDisplayLabel } from '../../../utils/nodeDisplayLabel';
import { useNodeInputColumns } from '../../../hooks/useNodeInputs';
import { useSchema, useNodeSchema } from '../../../contexts/SchemaContext';
import { Button } from '../../ui/Button';
import type { DynamicGroup } from '../../../types';

// ── Types ─────────────────────────────────────────────────────────────────────

export type TransformRuleType = 
  | 'datetime_format'
  | 'text_case'
  | 'currency_format'
  | 'chinese_number_format'
  | 'rmb_uppercase'
  | 'copy_column'
  | 'swap_columns'
  | 'transpose'
  | 'text_replace'
  | 'text_mask'
  | 'text_substring'
  | 'text_slice'
  | 'text_affix'
  | 'merge_columns'
  | 'extract_number'
  | 'split_to_columns';

export interface TransformRule {
  id: string;
  sourceColumn: string;
  sourceColumns?: string[];  // 用于多字段合并或交换列
  targetColumn: string;
  type: TransformRuleType;
  params: Record<string, string | number | boolean>;
  enabled: boolean;
}

interface TransformConfigProps {
  nodeId: string;
  config: { rules?: TransformRule[] };
}

// ── Rule Type Definitions ─────────────────────────────────────────────────────

interface RuleTypeDef {
  value: TransformRuleType;
  label: string;
  description: string;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  paramFields: ParamField[];
}

interface ParamField {
  name: string;
  label: string;
  type: 'select' | 'text' | 'number' | 'checkbox';
  options?: { value: string; label: string }[];
  placeholder?: string;
  defaultValue?: string | number | boolean;
  helpText?: string;
}

const RULE_TYPES: RuleTypeDef[] = [
  {
    value: 'copy_column',
    label: 'nodes.config.transform.copyColumn.label',
    description: 'nodes.config.transform.copyColumn.desc',
    icon: CopySimple,
    color: 'text-slate-600',
    bgColor: 'bg-slate-50',
    paramFields: [],
  },
  {
    value: 'swap_columns',
    label: 'nodes.config.transform.swapColumns.label',
    description: 'nodes.config.transform.swapColumns.desc',
    icon: ArrowsLeftRight,
    color: 'text-pink-600',
    bgColor: 'bg-pink-50',
    paramFields: [],
  },
  {
    value: 'transpose',
    label: 'nodes.config.transform.transpose.label',
    description: 'nodes.config.transform.transpose.desc',
    icon: ArrowsInLineVertical,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    paramFields: [
      {
        name: 'headerColumn',
        label: 'nodes.config.transform.transpose.headerColumn',
        type: 'select',
        options: [
          { value: 'none', label: 'nodes.config.transform.transpose.noHeader' },
          { value: 'first', label: 'nodes.config.transform.transpose.firstCol' },
        ],
        defaultValue: 'first',
        helpText: 'nodes.config.transform.transpose.headerColumnHelp',
      },
      {
        name: 'newHeaderName',
        label: 'nodes.config.transform.transpose.newHeaderName',
        type: 'text',
        placeholder: 'nodes.config.transform.transpose.newHeaderNamePlaceholder',
        defaultValue: '属性',
        helpText: 'nodes.config.transform.transpose.newHeaderNameHelp',
      },
    ],
  },
  {
    value: 'datetime_format',
    label: 'nodes.config.transform.datetimeFormat.label',
    description: 'nodes.config.transform.datetimeFormat.desc',
    icon: Calendar,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    paramFields: [
      {
        name: 'sourceFormat',
        label: 'nodes.config.transform.datetimeFormat.sourceFormat',
        type: 'select',
        options: [
          { value: 'auto', label: 'nodes.config.transform.common.autoDetect' },
          { value: 'YYYY-MM-DD', label: 'nodes.config.transform.datetimeFormat.formats.ymdDash' },
          { value: 'YYYY/MM/DD', label: 'nodes.config.transform.datetimeFormat.formats.ymdSlash' },
          { value: 'DD-MM-YYYY', label: 'nodes.config.transform.datetimeFormat.formats.dmyDash' },
          { value: 'MM/DD/YYYY', label: 'nodes.config.transform.datetimeFormat.formats.mdySlash' },
          { value: 'YYYY-MM-DD HH:mm:ss', label: 'nodes.config.transform.datetimeFormat.formats.ymdHmsDash' },
          { value: 'timestamp', label: 'nodes.config.transform.datetimeFormat.timestampMs' },
          { value: 'timestamp_sec', label: 'nodes.config.transform.datetimeFormat.timestampSec' },
        ],
        defaultValue: 'auto',
        helpText: 'nodes.config.transform.datetimeFormat.sourceFormatHelp',
      },
      {
        name: 'targetFormat',
        label: 'nodes.config.transform.datetimeFormat.targetFormat',
        type: 'select',
        options: [
          { value: 'YYYY-MM-DD', label: 'nodes.config.transform.datetimeFormat.formats.ymdDash' },
          { value: 'YYYY/MM/DD', label: 'nodes.config.transform.datetimeFormat.formats.ymdSlash' },
          { value: 'DD-MM-YYYY', label: 'nodes.config.transform.datetimeFormat.formats.dmyDash' },
          { value: 'MM/DD/YYYY', label: 'nodes.config.transform.datetimeFormat.formats.mdySlash' },
          { value: 'YYYY年MM月DD日', label: 'nodes.config.transform.datetimeFormat.formats.ymdCn' },
          { value: 'YYYY-MM-DD HH:mm:ss', label: 'nodes.config.transform.datetimeFormat.formats.ymdHmsDash' },
          { value: 'YYYY/MM/DD HH:mm', label: 'nodes.config.transform.datetimeFormat.formats.ymdHmSlash' },
          { value: 'timestamp', label: 'nodes.config.transform.datetimeFormat.timestampMs' },
          { value: 'timestamp_sec', label: 'nodes.config.transform.datetimeFormat.timestampSec' },
        ],
        defaultValue: 'YYYY-MM-DD',
        helpText: 'nodes.config.transform.datetimeFormat.targetFormatHelp',
      },
    ],
  },
  {
    value: 'text_case',
    label: 'nodes.config.transform.textCase.label',
    description: 'nodes.config.transform.textCase.desc',
    icon: TextT,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    paramFields: [
      {
        name: 'caseType',
        label: 'nodes.config.transform.textCase.caseType',
        type: 'select',
        options: [
          { value: 'upper', label: 'nodes.config.transform.textCase.upper' },
          { value: 'lower', label: 'nodes.config.transform.textCase.lower' },
          { value: 'capitalize', label: 'nodes.config.transform.textCase.capitalize' },
          { value: 'title', label: 'nodes.config.transform.textCase.title' },
        ],
        defaultValue: 'upper',
      },
    ],
  },
  {
    value: 'currency_format',
    label: 'nodes.config.transform.currency.label',
    description: 'nodes.config.transform.currency.desc',
    icon: CurrencyDollar,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    paramFields: [
      {
        name: 'currency',
        label: 'nodes.config.transform.currency.currency',
        type: 'select',
        options: [
          { value: 'CNY', label: 'nodes.config.transform.currency.cny' },
          { value: 'USD', label: 'nodes.config.transform.currency.usd' },
          { value: 'EUR', label: 'nodes.config.transform.currency.eur' },
          { value: 'GBP', label: 'nodes.config.transform.currency.gbp' },
          { value: 'JPY', label: 'nodes.config.transform.currency.jpy' },
          { value: 'none', label: 'nodes.config.transform.currency.none' },
        ],
        defaultValue: 'CNY',
      },
      {
        name: 'decimals',
        label: 'nodes.config.transform.currency.decimals',
        type: 'select',
        options: [
          { value: '0', label: 'nodes.config.transform.common.decimals0' },
          { value: '1', label: 'nodes.config.transform.common.decimals1' },
          { value: '2', label: 'nodes.config.transform.common.decimals2' },
          { value: '3', label: 'nodes.config.transform.common.decimals3' },
          { value: '4', label: 'nodes.config.transform.common.decimals4' },
        ],
        defaultValue: '2',
      },
      {
        name: 'thousandsSeparator',
        label: 'nodes.config.transform.currency.thousands',
        type: 'select',
        options: [
          { value: 'true', label: 'nodes.config.transform.common.yes' },
          { value: 'false', label: 'nodes.config.transform.common.no' },
        ],
        defaultValue: 'true',
      },
    ],
  },
  {
    value: 'chinese_number_format',
    label: 'nodes.config.transform.cnNumber.label',
    description: 'nodes.config.transform.cnNumber.desc',
    icon: CurrencyDollar,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    paramFields: [
      {
        name: 'style',
        label: 'nodes.config.transform.cnNumber.style',
        type: 'select',
        options: [
          { value: 'uppercase', label: 'nodes.config.transform.cnNumber.uppercase' },
          { value: 'thousands', label: 'nodes.config.transform.cnNumber.thousands' },
          { value: 'wan', label: 'nodes.config.transform.cnNumber.wan' },
          { value: 'yi', label: 'nodes.config.transform.cnNumber.yi' },
          { value: 'accounting', label: 'nodes.config.transform.cnNumber.accounting' },
        ],
        defaultValue: 'thousands',
      },
      {
        name: 'decimals',
        label: 'nodes.config.transform.currency.decimals',
        type: 'select',
        options: [
          { value: '0', label: 'nodes.config.transform.common.decimals0' },
          { value: '1', label: 'nodes.config.transform.common.decimals1' },
          { value: '2', label: 'nodes.config.transform.common.decimals2' },
          { value: '3', label: 'nodes.config.transform.common.decimals3' },
          { value: '4', label: 'nodes.config.transform.common.decimals4' },
        ],
        defaultValue: '2',
      },
    ],
  },
  {
    value: 'rmb_uppercase',
    label: 'nodes.config.transform.rmbUppercase.label',
    description: 'nodes.config.transform.rmbUppercase.desc',
    icon: CurrencyDollar,
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    paramFields: [
      {
        name: 'conversion',
        label: 'nodes.config.transform.rmbUppercase.conversion',
        type: 'select',
        options: [
          { value: 'num_to_upper', label: 'nodes.config.transform.rmbUppercase.numToUpper' },
          { value: 'upper_to_num', label: 'nodes.config.transform.rmbUppercase.upperToNum' },
        ],
        defaultValue: 'num_to_upper',
        helpText: 'nodes.config.transform.rmbUppercase.conversionHelp',
      },
    ],
  },
  {
    value: 'text_replace',
    label: 'nodes.config.transform.textReplace.label',
    description: 'nodes.config.transform.textReplace.desc',
    icon: Swap,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    paramFields: [
      {
        name: 'search',
        label: 'nodes.config.transform.textReplace.search',
        type: 'text',
        placeholder: 'nodes.config.transform.textReplace.searchPlaceholder',
      },
      {
        name: 'replace',
        label: 'nodes.config.transform.textReplace.replace',
        type: 'text',
        placeholder: 'nodes.config.transform.textReplace.replacePlaceholder',
      },
      {
        name: 'regex',
        label: 'nodes.config.transform.textReplace.regex',
        type: 'select',
        options: [
          { value: 'false', label: 'nodes.config.transform.common.no' },
          { value: 'true', label: 'nodes.config.transform.common.yes' },
        ],
        defaultValue: 'false',
      },
      {
        name: 'global',
        label: 'nodes.config.transform.textReplace.global',
        type: 'select',
        options: [
          { value: 'true', label: 'nodes.config.transform.common.yes' },
          { value: 'false', label: 'nodes.config.transform.common.no' },
        ],
        defaultValue: 'true',
      },
    ],
  },
  {
    value: 'text_mask',
    label: 'nodes.config.transform.textMask.label',
    description: 'nodes.config.transform.textMask.desc',
    icon: EyeSlash,
    color: 'text-rose-600',
    bgColor: 'bg-rose-50',
    paramFields: [
      {
        name: 'maskType',
        label: 'nodes.config.transform.textMask.type',
        type: 'select',
        options: [
          { value: 'phone', label: 'nodes.config.transform.textMask.phone' },
          { value: 'idcard', label: 'nodes.config.transform.textMask.idcard' },
          { value: 'bankcard', label: 'nodes.config.transform.textMask.bankcard' },
          { value: 'email', label: 'nodes.config.transform.textMask.email' },
          { value: 'name', label: 'nodes.config.transform.textMask.name' },
          { value: 'custom', label: 'nodes.config.transform.textMask.custom' },
        ],
        defaultValue: 'phone',
      },
      {
        name: 'maskChar',
        label: 'nodes.config.transform.textMask.maskChar',
        type: 'text',
        placeholder: '*',
        defaultValue: '*',
      },
      {
        name: 'keepStart',
        label: 'nodes.config.transform.textMask.keepStart',
        type: 'number',
        placeholder: '0',
        defaultValue: 0,
      },
      {
        name: 'keepEnd',
        label: 'nodes.config.transform.textMask.keepEnd',
        type: 'number',
        placeholder: '0',
        defaultValue: 0,
      },
    ],
  },
  {
    value: 'text_substring',
    label: 'nodes.config.transform.substring.label',
    description: 'nodes.config.transform.substring.desc',
    icon: Textbox,
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-50',
    paramFields: [
      {
        name: 'start',
        label: 'nodes.config.transform.substring.start',
        type: 'number',
        placeholder: '0',
        defaultValue: 0,
        helpText: 'nodes.config.transform.substring.startHelp',
      },
      {
        name: 'length',
        label: 'nodes.config.transform.substring.length',
        type: 'number',
        placeholder: '10',
        defaultValue: 10,
      },
      {
        name: 'ellipsis',
        label: 'nodes.config.transform.substring.ellipsis',
        type: 'select',
        options: [
          { value: 'false', label: 'nodes.config.transform.common.no' },
          { value: 'true', label: 'nodes.config.transform.common.yes' },
        ],
        defaultValue: 'false',
      },
    ],
  },
  {
    value: 'text_slice',
    label: 'nodes.config.transform.slice.label',
    description: 'nodes.config.transform.slice.desc',
    icon: Scissors,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    paramFields: [
      {
        name: 'start',
        label: 'nodes.config.transform.slice.start',
        type: 'number',
        placeholder: '0',
        defaultValue: 0,
        helpText: 'nodes.config.transform.slice.startHelp',
      },
      {
        name: 'end',
        label: 'nodes.config.transform.slice.end',
        type: 'number',
        placeholder: 'nodes.config.transform.slice.endPlaceholder',
        helpText: 'nodes.config.transform.slice.endHelp',
      },
    ],
  },
  {
    value: 'text_affix',
    label: 'nodes.config.transform.affix.label',
    description: 'nodes.config.transform.affix.desc',
    icon: TextT,
    color: 'text-teal-600',
    bgColor: 'bg-teal-50',
    paramFields: [
      {
        name: 'position',
        label: 'nodes.config.transform.affix.position',
        type: 'select',
        options: [
          { value: 'prefix', label: 'nodes.config.transform.affix.prefix' },
          { value: 'suffix', label: 'nodes.config.transform.affix.suffix' },
        ],
        defaultValue: 'prefix',
      },
      {
        name: 'text',
        label: 'nodes.config.transform.affix.text',
        type: 'text',
        placeholder: 'nodes.config.transform.affix.textPlaceholder',
        helpText: 'nodes.config.transform.affix.textHelp',
      },
    ],
  },
  {
    value: 'merge_columns',
    label: 'nodes.config.transform.mergeColumns.label',
    description: 'nodes.config.transform.mergeColumns.desc',
    icon: ArrowsClockwise,
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
    paramFields: [
      {
        name: 'separator',
        label: 'nodes.config.transform.mergeColumns.separator',
        type: 'text',
        placeholder: 'nodes.config.transform.mergeColumns.separatorPlaceholder',
        defaultValue: '',
        helpText: 'nodes.config.transform.mergeColumns.separatorHelp',
      },
      {
        name: 'ignoreEmpty',
        label: 'nodes.config.transform.mergeColumns.ignoreEmpty',
        type: 'select',
        options: [
          { value: 'true', label: 'nodes.config.transform.common.yes' },
          { value: 'false', label: 'nodes.config.transform.common.no' },
        ],
        defaultValue: 'true',
        helpText: 'nodes.config.transform.mergeColumns.ignoreEmptyHelp',
      },
    ],
  },
  {
    value: 'extract_number',
    label: 'nodes.config.transform.extractNumber.label',
    description: 'nodes.config.transform.extractNumber.desc',
    icon: NumberSquareOne,
    color: 'text-lime-600',
    bgColor: 'bg-lime-50',
    paramFields: [
      {
        name: 'extractMode',
        label: 'nodes.config.transform.extractNumber.extractMode',
        type: 'select',
        options: [
          { value: 'all', label: 'nodes.config.transform.extractNumber.all' },
          { value: 'first', label: 'nodes.config.transform.extractNumber.first' },
        ],
        defaultValue: 'all',
        helpText: 'nodes.config.transform.extractNumber.extractModeHelp',
      },
      {
        name: 'decimal',
        label: 'nodes.config.transform.extractNumber.decimal',
        type: 'select',
        options: [
          { value: 'keep', label: 'nodes.config.transform.extractNumber.keep' },
          { value: 'integer', label: 'nodes.config.transform.extractNumber.integer' },
        ],
        defaultValue: 'keep',
        helpText: 'nodes.config.transform.extractNumber.decimalHelp',
      },
      {
        name: 'multiValueMode',
        label: 'nodes.config.transform.extractNumber.multiValueMode',
        type: 'select',
        options: [
          { value: 'merge', label: 'nodes.config.transform.extractNumber.multiValueMerge' },
          { value: 'suffix', label: 'nodes.config.transform.extractNumber.multiValueSuffix' },
        ],
        defaultValue: 'merge',
        helpText: 'nodes.config.transform.extractNumber.multiValueModeHelp',
      },
      {
        name: 'maxSuffixColumns',
        label: 'nodes.config.transform.extractNumber.maxSuffixColumns',
        type: 'number',
        defaultValue: 10,
        helpText: 'nodes.config.transform.extractNumber.maxSuffixColumnsHelp',
      },
    ],
  },
  {
    value: 'split_to_columns',
    label: 'nodes.config.transform.splitToColumns.label',
    description: 'nodes.config.transform.splitToColumns.desc',
    icon: SplitHorizontal,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    paramFields: [
      {
        name: 'separator',
        label: 'nodes.config.transform.splitToColumns.separator',
        type: 'text',
        placeholder: 'nodes.config.transform.splitToColumns.separatorPlaceholder',
        defaultValue: ',',
        helpText: 'nodes.config.transform.splitToColumns.separatorHelp',
      },
      {
        name: 'limit',
        label: 'nodes.config.transform.splitToColumns.limit',
        type: 'number',
        placeholder: '0',
        defaultValue: 0,
        helpText: 'nodes.config.transform.splitToColumns.limitHelp',
      },
    ],
  },
];

// ── Helper Functions ──────────────────────────────────────────────────────────

const generateId = () => `rule_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const getDefaultParams = (ruleType: TransformRuleType): Record<string, string | number | boolean> => {
  const ruleDef = RULE_TYPES.find(t => t.value === ruleType);
  const params: Record<string, string | number | boolean> = {};
  ruleDef?.paramFields.forEach(field => {
    if (field.defaultValue !== undefined) {
      params[field.name] = field.defaultValue;
    }
  });
  return params;
};

// ── Preview Component ─────────────────────────────────────────────────────────

function TransformPreview({ 
  sampleValue, 
  rule 
}: { 
  sampleValue: string; 
  rule: TransformRule;
}) {
  const preview = useMemo(() => {
    if (!sampleValue) return '';
    
    try {
      switch (rule.type) {
        case 'datetime_format': {
          const targetFormat = rule.params.targetFormat as string || 'YYYY-MM-DD';
          return `Preview → ${targetFormat}`;
        }
        case 'text_case': {
          const caseType = rule.params.caseType as string || 'upper';
          switch (caseType) {
            case 'upper': return sampleValue.toUpperCase();
            case 'lower': return sampleValue.toLowerCase();
            case 'capitalize': 
              return sampleValue.charAt(0).toUpperCase() + sampleValue.slice(1).toLowerCase();
            case 'title':
              return sampleValue.replace(/\b\w/g, c => c.toUpperCase());
            default: return sampleValue;
          }
        }
        case 'copy_column': {
          return sampleValue;
        }
        case 'swap_columns': {
          const cols = rule.sourceColumns || [rule.sourceColumn, ''];
          return `${cols[0] || '?'} ↔ ${cols[1] || '?'}`;
        }
        case 'currency_format': {
          const currency = rule.params.currency as string || 'CNY';
          const decimals = parseInt(rule.params.decimals as string || '2');
          const symbols: Record<string, string> = { CNY: '¥', USD: '$', EUR: '€', GBP: '£', JPY: '¥', none: '' };
          const num = parseFloat(sampleValue);
          if (isNaN(num)) return sampleValue;
          return `${symbols[currency] || ''}${num.toFixed(decimals)}`;
        }
        case 'chinese_number_format': {
          const style = rule.params.style as string || 'thousands';
          const decimals = parseInt(rule.params.decimals as string || '2');
          const num = parseFloat(sampleValue);
          if (isNaN(num)) return sampleValue;
          if (style === 'wan') return `${(num / 1e4).toFixed(decimals)}`;
          if (style === 'yi') return `${(num / 1e8).toFixed(decimals)}`;
          if (style === 'uppercase') {
            return 'Uppercase amount (generated at runtime)';
          }
          if (style === 'accounting') {
            const n = num;
            const s = n >= 0 ? n.toFixed(decimals) : `(${Math.abs(n).toFixed(decimals)})`;
            const parts = s.replace(/^\(?|\)?$/g, '').split('.');
            const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            return num >= 0 ? intPart + (parts[1] ? '.' + parts[1] : '') : '(' + intPart + (parts[1] ? '.' + parts[1] : '') + ')';
          }
          const fixed = num.toFixed(decimals);
          const [intPart, decPart] = fixed.split('.');
          const withComma = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
          return (decPart ? withComma + '.' + decPart : withComma);
        }
        case 'rmb_uppercase': {
          const conversion = rule.params.conversion as string || 'num_to_upper';
          if (conversion === 'num_to_upper') {
            return '壹佰贰拾叁万肆仟伍佰陆拾柒元捌角玖分 (generated at runtime)';
          } else {
            return '1234567.89 (generated at runtime)';
          }
        }
        case 'text_replace': {
          const search = rule.params.search as string || '';
          const replace = rule.params.replace as string || '';
          if (!search) return sampleValue;
          return sampleValue.split(search).join(replace);
        }
        case 'text_mask': {
          const maskType = rule.params.maskType as string || 'phone';
          const maskChar = rule.params.maskChar as string || '*';
          if (maskType === 'phone' && sampleValue.length >= 7) {
            return sampleValue.slice(0, 3) + maskChar.repeat(4) + sampleValue.slice(-4);
          }
          if (maskType === 'name' && sampleValue.length >= 2) {
            return sampleValue.charAt(0) + maskChar.repeat(sampleValue.length - 1);
          }
          return sampleValue;
        }
        case 'text_substring': {
          const start = parseInt(rule.params.start as string || '0');
          const length = parseInt(rule.params.length as string || '10');
          const ellipsis = rule.params.ellipsis === 'true';
          const result = sampleValue.slice(start, start + length);
          return ellipsis && start + length < sampleValue.length ? result + '...' : result;
        }
        case 'text_slice': {
          const start = parseInt(rule.params.start as string || '0');
          const end = rule.params.end ? parseInt(rule.params.end as string) : undefined;
          return sampleValue.slice(start, end);
        }
        case 'text_affix': {
          const position = rule.params.position as string || 'prefix';
          const text = rule.params.text as string || '';
          if (position === 'prefix') {
            return text + sampleValue;
          } else {
            return sampleValue + text;
          }
        }
        case 'merge_columns': {
          return 'Preview of merged columns (generated at runtime)';
        }
        case 'extract_number': {
          const extractMode = rule.params.extractMode as string || 'all';
          const decimal = rule.params.decimal as string || 'keep';
          const multiValueMode = rule.params.multiValueMode as string || 'merge';
          const base = (rule.targetColumn?.trim() || rule.sourceColumn || 'col');
          const pattern = decimal === 'integer' ? /[-+]?\d+/g : /[-+]?\d+\.?\d*/g;
          const matches = sampleValue.match(pattern);

          if (!matches || matches.length === 0) return '';

          if (extractMode === 'first') {
            return multiValueMode === 'suffix' ? `${matches[0]} → ${base}_1` : matches[0];
          }
          if (matches.length === 1) {
            return multiValueMode === 'suffix' ? `${matches[0]} → ${base}_1` : matches[0];
          }
          if (multiValueMode === 'suffix') {
            const suffixNames = matches.slice(0, 5).map((_, i) => `${base}_${i + 1}`).join(', ');
            const more = matches.length > 5 ? '…' : '';
            return `${matches.join(' | ')} → ${suffixNames}${more}`;
          }
          return `[${matches.join(', ')}]`;
        }
        case 'split_to_columns': {
          const separator = rule.params.separator as string || ',';
          const limit = parseInt(rule.params.limit as string || '0') || 0;
          
          // 处理特殊分隔符
          let actualSeparator = separator;
          if (separator === '\\t') actualSeparator = '\t';
          if (separator === '\\n') actualSeparator = '\n';
          
          const parts = sampleValue.split(actualSeparator, limit || undefined);
          return parts.join(' | ');
        }
        default: return sampleValue;
      }
    } catch {
      return sampleValue;
    }
  }, [sampleValue, rule]);

  if (!sampleValue) return null;

  return (
    <div className="mt-2 p-2 bg-zinc-50 rounded-lg border border-zinc-100">
      <div className="flex items-center gap-1.5 text-[10px] text-zinc-400 mb-1">
        <Eye className="w-3 h-3" />
        <span>Preview</span>
      </div>
      <div className="text-xs text-zinc-600 font-mono break-all">
        {preview}
      </div>
    </div>
  );
}

// ── Rule Card Component ───────────────────────────────────────────────────────



interface RuleCardProps {
  rule: TransformRule;
  index: number;
  availableColumns: string[];
  isConnected: boolean;
  hasRealColumns: boolean;
  onUpdate: (updates: Partial<TransformRule>) => void;
  onRemove: () => void;
  t: (key: string) => string;
}

function RuleCard({ rule, index, availableColumns, isConnected, hasRealColumns, onUpdate, onRemove, t }: RuleCardProps) {
  const [expanded, setExpanded] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [sampleValue, setSampleValue] = useState('');
  
  const ruleDef = RULE_TYPES.find(t => t.value === rule.type);
  const Icon = ruleDef?.icon || ArrowsClockwise;

  const collapsedSummary = useMemo(() => {
    const typeLabel = ruleDef ? t(ruleDef.label) : rule.type;
    let detail = '';
    if (rule.type === 'swap_columns') {
      const a = rule.sourceColumns?.[0]?.trim() || '—';
      const b = rule.sourceColumns?.[1]?.trim() || '—';
      detail = `${a} ↔ ${b}`;
    } else if (rule.type === 'merge_columns') {
      const cols = rule.sourceColumns?.filter(Boolean).length
        ? rule.sourceColumns!.join(', ')
        : '—';
      const tgt = rule.targetColumn?.trim();
      detail = tgt ? `${cols} → ${tgt}` : `${cols} → ${t('nodes.config.transform.summaryOverwrite')}`;
    } else {
      const src = rule.sourceColumn?.trim() || '—';
      const tgt = rule.targetColumn?.trim();
      detail = tgt ? `${src} → ${tgt}` : `${src} → ${t('nodes.config.transform.summaryOverwrite')}`;
    }
    return `${typeLabel} · ${detail}`;
  }, [rule, ruleDef, t]);

  useEffect(() => {
    if (!expanded) setShowPreview(false);
  }, [expanded]);
  
  const handleTypeChange = (newType: TransformRuleType) => {
    onUpdate({ 
      type: newType, 
      params: getDefaultParams(newType) 
    });
  };

  const updateParam = (name: string, value: string | number | boolean) => {
    onUpdate({
      params: { ...rule.params, [name]: value }
    });
  };

  return (
    <div className={`rounded-xl border overflow-hidden transition-all ${
        rule.enabled 
          ? 'bg-white border-zinc-200 shadow-sm' 
          : 'bg-zinc-50 border-zinc-100 opacity-60'
      }`}
    >
      {/* Header */}
      <div
        className={`px-3 py-2 flex items-start justify-between gap-2 ${expanded ? 'border-b' : ''} ${
          ruleDef?.bgColor || 'bg-zinc-50'
        } ${!expanded ? 'rounded-b-xl' : ''}`}
      >
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="flex items-start gap-2 min-w-0 flex-1 text-left rounded-lg -my-0.5 -ml-1 pl-1 py-0.5 pr-2 hover:bg-black/[0.04] transition-colors"
          title={expanded ? t('nodes.config.transform.collapseRule') : t('nodes.config.transform.expandRule')}
        >
          <span className="mt-0.5 flex-shrink-0 text-zinc-500">
            {expanded ? (
              <CaretDown className="w-4 h-4" weight="bold" />
            ) : (
              <CaretRight className="w-4 h-4" weight="bold" />
            )}
          </span>
          <div
            className={`w-6 h-6 rounded-lg flex-shrink-0 flex items-center justify-center ${
              ruleDef?.bgColor || 'bg-zinc-100'
            }`}
          >
            <Icon className={`w-3.5 h-3.5 ${ruleDef?.color || 'text-zinc-500'}`} weight="fill" />
          </div>
          <div className="min-w-0 flex-1">
            <span className="text-xs font-medium text-zinc-700">
              {(t as (key: string, opts?: Record<string, unknown>) => string)('nodes.config.transform.ruleTitle', {
                index: index + 1,
              })}
            </span>
            {!expanded && (
              <p className="text-[10px] text-zinc-500 mt-0.5 leading-snug break-words line-clamp-2">
                {collapsedSummary}
              </p>
            )}
          </div>
        </button>
        <div className="flex items-center gap-1 flex-shrink-0 pt-0.5">
          <button
            type="button"
            onClick={() => onUpdate({ enabled: !rule.enabled })}
            className={`p-1 rounded transition-colors ${
              rule.enabled
                ? 'text-emerald-600 hover:bg-emerald-100'
                : 'text-zinc-400 hover:bg-zinc-200'
            }`}
            title={rule.enabled ? t('nodes.config.transform.disableRule') : t('nodes.config.transform.enableRule')}
          >
            <Check className="w-3.5 h-3.5" weight="bold" />
          </button>
          <button
            type="button"
            onClick={() => setShowPreview(!showPreview)}
            disabled={!expanded}
            className={`p-1 rounded transition-colors ${
              !expanded
                ? 'text-zinc-300 cursor-not-allowed'
                : showPreview
                  ? 'text-blue-600 hover:bg-blue-100'
                  : 'text-zinc-400 hover:bg-zinc-200'
            }`}
            title={t('nodes.config.transform.previewButton')}
          >
            <Play className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="p-1 text-zinc-400 hover:text-rose-500 hover:bg-rose-50 rounded transition-colors"
            title={t('nodes.config.transform.deleteRule') as string}
          >
            <Trash className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Content */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-3 space-y-3">
        {/* Rule Type Selector */}
        <div>
          <label className="text-[10px] font-medium text-zinc-500 uppercase tracking-wider mb-1.5 block">
            {t('nodes.config.transform.type')}
          </label>
          <div className="grid grid-cols-2 gap-1.5">
            {RULE_TYPES.map((type) => (
              <button
                key={type.value}
                onClick={() => handleTypeChange(type.value)}
                className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg border text-left transition-all ${
                  rule.type === type.value
                    ? `${type.bgColor} ${type.color} border-current`
                    : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300'
                }`}
                >
                  <type.icon className="w-3.5 h-3.5 flex-shrink-0" weight="fill" />
                  <span className="text-[10px] font-medium truncate">
                    {t(type.label)}
                  </span>
              </button>
            ))}
          </div>
          <p className="mt-1.5 text-[10px] text-zinc-400">
            {ruleDef ? t(ruleDef.description) : ''}
          </p>
        </div>

        {/* Column Selection */}
        <div className={rule.type === 'merge_columns' || rule.type === 'swap_columns' ? 'space-y-3' : 'grid grid-cols-2 gap-3'}>
          <div>
            <label className="text-[10px] font-medium text-zinc-500 uppercase tracking-wider mb-1.5 block">
              {rule.type === 'merge_columns'
                ? t('nodes.config.transform.mergeColumns.sourceColumns')
                : rule.type === 'swap_columns'
                  ? t('nodes.config.transform.swapColumns.selectColumns')
                  : t('nodes.config.transform.sourceColumn')}
            </label>
            {rule.type === 'swap_columns' ? (
              // 交换列：选择两个列
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  {/* 第一列选择 */}
                  <select
                    value={rule.sourceColumns?.[0] || ''}
                    onChange={(e) => {
                      const current = rule.sourceColumns || ['', ''];
                      onUpdate({ sourceColumns: [e.target.value, current[1] || ''] });
                    }}
                    className="w-full px-2.5 py-1.5 text-xs bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  >
                    <option value="">{t('nodes.config.transform.swapColumns.column1')}</option>
                    {availableColumns.map(col => (
                      <option key={col} value={col}>{col}</option>
                    ))}
                  </select>
                  {/* 第二列选择 */}
                  <select
                    value={rule.sourceColumns?.[1] || ''}
                    onChange={(e) => {
                      const current = rule.sourceColumns || ['', ''];
                      onUpdate({ sourceColumns: [current[0] || '', e.target.value] });
                    }}
                    className="w-full px-2.5 py-1.5 text-xs bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  >
                    <option value="">{t('nodes.config.transform.swapColumns.column2')}</option>
                    {availableColumns.map(col => (
                      <option key={col} value={col}>{col}</option>
                    ))}
                  </select>
                </div>
                {/* 交换预览 */}
                {rule.sourceColumns?.[0] && rule.sourceColumns?.[1] && (
                  <div className="p-2 bg-pink-50 rounded-lg border border-pink-100">
                    <div className="text-[10px] text-pink-600 mb-1 font-medium">
                      {t('nodes.config.transform.swapColumns.preview')}
                    </div>
                    <div className="flex items-center justify-center gap-2 text-xs">
                      <span className="px-2 py-1 bg-white text-pink-700 rounded border border-pink-200">
                        {rule.sourceColumns[0]}
                      </span>
                      <ArrowsLeftRight className="w-3.5 h-3.5 text-pink-500" />
                      <span className="px-2 py-1 bg-white text-pink-700 rounded border border-pink-200">
                        {rule.sourceColumns[1]}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ) : rule.type === 'merge_columns' ? (
              // 多字段合并：多选，按勾选顺序
              <div className="space-y-2">
                <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto border border-zinc-200 rounded-lg p-2 bg-white">
                  {availableColumns.length > 0 ? (
                    availableColumns.map(col => {
                      const isChecked = rule.sourceColumns?.includes(col) || false;
                      const order = rule.sourceColumns?.indexOf(col);
                      return (
                        <button
                          key={col}
                          onClick={() => {
                            const current = rule.sourceColumns || [];
                            if (current.includes(col)) {
                              // 取消勾选
                              onUpdate({ sourceColumns: current.filter(c => c !== col) });
                            } else {
                              // 勾选，添加到末尾
                              onUpdate({ sourceColumns: [...current, col] });
                            }
                          }}
                          className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs transition-all ${
                            isChecked
                              ? 'bg-violet-100 text-violet-700 border border-violet-300'
                              : 'bg-zinc-50 text-zinc-600 border border-zinc-200 hover:bg-zinc-100'
                          }`}
                        >
                          {isChecked && (
                            <span className="flex items-center justify-center w-4 h-4 text-[10px] font-bold bg-violet-500 text-white rounded-full">
                              {order! + 1}
                            </span>
                          )}
                          <span className="truncate max-w-[100px]">{col}</span>
                        </button>
                      );
                    })
                  ) : (
                    <span className="text-xs text-zinc-400">
                      {t('nodes.config.transform.noColumns')}
                    </span>
                  )}
                </div>
                {/* 已选字段顺序预览 */}
                {rule.sourceColumns && rule.sourceColumns.length > 0 && (
                  <div className="p-2 bg-violet-50 rounded-lg border border-violet-100">
                    <div className="text-[10px] text-violet-600 mb-1.5 font-medium">
                      {t('nodes.config.transform.mergeColumns.orderPreview')}
                    </div>
                    <div className="flex items-center gap-1 flex-wrap">
                      {rule.sourceColumns.map((col, idx) => (
                        <div key={col} className="flex items-center gap-1">
                          <span className="px-2 py-0.5 bg-white text-violet-700 text-xs rounded border border-violet-200">
                            {idx + 1}. {col}
                          </span>
                          {idx < (rule.sourceColumns?.length || 0) - 1 && (
                            <span className="text-violet-400 text-xs">
                              {rule.params.separator || ' +'}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 pt-2 border-t border-violet-100">
                      <div className="text-[10px] text-violet-500 font-mono break-all">
                        {t('nodes.config.transform.mergeColumns.example')} {rule.sourceColumns.map((col, idx) => (
                          <span key={col}>
                            [{col}]
                            {idx < (rule.sourceColumns?.length || 0) - 1 ? (rule.params.separator as string || '') : ''}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : hasRealColumns ? (
              <select
                value={rule.sourceColumn}
                onChange={(e) => onUpdate({ sourceColumn: e.target.value })}
                className="w-full px-2.5 py-1.5 text-xs bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
              >
                <option value="">{t('nodes.config.transform.selectColumnPlaceholder')}</option>
                {availableColumns.map(col => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            ) : (
                    <input
                    type="text"
                value={rule.sourceColumn}
                onChange={(e) => onUpdate({ sourceColumn: e.target.value })}
                placeholder={
                  isConnected
                    ? t('nodes.config.transform.sourceManualPlaceholder')
                    : t('nodes.config.transform.connectInputFirst')
                }
                disabled={!isConnected}
                className="w-full px-2.5 py-1.5 text-xs bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 disabled:bg-zinc-100 disabled:text-zinc-400"
              />
            )}
          </div>
          {rule.type !== 'swap_columns' && (
            <div>
              <label className="text-[10px] font-medium text-zinc-500 uppercase tracking-wider mb-1.5 block">
                {t('nodes.config.transform.targetColumn')}
              </label>
              <input
                type="text"
                value={rule.targetColumn}
                onChange={(e) => onUpdate({ targetColumn: e.target.value })}
                placeholder={
                  rule.type === 'merge_columns'
                    ? t('nodes.config.transform.targetNewColumnPlaceholder')
                    : rule.type === 'copy_column'
                      ? (t as (key: string, opts?: Record<string, unknown>) => string)(
                          'nodes.config.transform.copyColumn.targetPlaceholder',
                          { source: (rule.sourceColumn || 'col').trim() }
                        )
                    : t('nodes.config.transform.targetPlaceholder')
                }
                className="w-full px-2.5 py-1.5 text-xs bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
              />
            </div>
          )}
        </div>

        {/* Parameters */}
        {ruleDef?.paramFields && ruleDef.paramFields.length > 0 && (
          <div className="space-y-2 pt-2 border-t border-zinc-100">
            <label className="text-[10px] font-medium text-zinc-500 uppercase tracking-wider">
              {t('nodes.config.transform.params')}
            </label>
            <div className="grid grid-cols-1 gap-2">
              {ruleDef.paramFields.map((field) => (
                <div key={field.name} className="flex items-center gap-2 min-w-0">
                  <label className="text-xs text-zinc-600 w-20 flex-shrink-0">
                    {t(field.label)}
                  </label>
                  {field.type === 'select' ? (
                    <select
                      value={String(rule.params[field.name] ?? field.defaultValue ?? '')}
                      onChange={(e) => updateParam(field.name, e.target.value)}
                      className="flex-1 min-w-0 px-2 py-1 text-xs bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                    >
                      {field.options?.map(opt => (
                        <option key={opt.value} value={opt.value}>{t(opt.label)}</option>
                      ))}
                    </select>
                  ) : field.type === 'number' ? (
                    <input
                      type="number"
                      value={(rule.params[field.name] as number | undefined) ?? (field.defaultValue as number | undefined) ?? ''}
                      onChange={(e) => updateParam(field.name, e.target.value === '' ? 0 : Number(e.target.value))}
                      placeholder={t(field.placeholder ?? '')}
                      className="flex-1 min-w-0 px-2 py-1 text-xs bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                    />
                  ) : (
                    <input
                      type={field.type === 'checkbox' ? 'checkbox' : 'text'}
                      checked={field.type === 'checkbox' ? (rule.params[field.name] as boolean) : undefined}
                      value={field.type !== 'checkbox' ? String(rule.params[field.name] ?? field.defaultValue ?? '') : undefined}
                      onChange={(e) => updateParam(field.name, field.type === 'checkbox' ? e.target.checked : e.target.value)}
                      placeholder={t(field.placeholder ?? '')}
                      className="flex-1 min-w-0 px-2 py-1 text-xs bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Preview Section */}
        <AnimatePresence>
          {showPreview && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="pt-2 border-t border-zinc-100">
                <div className="flex items-center gap-2 mb-2 min-w-0">
                  <input
                    type="text"
                    value={sampleValue}
                    onChange={(e) => setSampleValue(e.target.value)}
                    placeholder={t('nodes.config.transform.previewPlaceholder')}
                    className="flex-1 min-w-0 px-2 py-1 text-xs bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  />
                </div>
                {sampleValue && <TransformPreview sampleValue={sampleValue} rule={rule} />}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export function TransformConfig({ nodeId, config }: TransformConfigProps) {
  const { t, i18n } = useTranslation();
  const store = useWorkflowStore();
  const { register } = useNodeSchema(nodeId);
  const { getNodeSchema } = useSchema();
  const inputColumns = useNodeInputColumns(nodeId);
  const upstreamNodeId = useMemo(
    () => store.edges.find(e => e.target === nodeId)?.source,
    [store.edges, nodeId]
  );
  
  // 确保 config 有默认值
  const safeConfig = config || {};
  const rules = useMemo(() => safeConfig.rules || [], [safeConfig.rules]);
  
  // 检查是否有实际的列数据（排除占位符）
  const hasRealColumns = useMemo(() => 
    inputColumns?.some(c => c.column.name !== '') ?? false,
    [inputColumns]
  );
  
  // 获取上游的原始列和可用列名列表
  const upstreamColumns = useMemo(
    () => (inputColumns ?? []).map(c => c.column),
    [inputColumns]
  );

  const availableColumns = useMemo(
    () => upstreamColumns.map(c => c.name),
    [upstreamColumns]
  );

  // 检查是否已连接输入节点（通过 edges 判断）
  const isConnected = useMemo(() => {
    return store.edges.some(edge => edge.target === nodeId);
  }, [store.edges, nodeId]);

  // 获取上游节点信息
  const sourceInfo = useMemo(() => {
    if (!inputColumns || inputColumns.length === 0) return null;
    const source = inputColumns[0].source;
    const columnCount = availableColumns.length;
    if (columnCount > 0) {
      return t('nodes.config.transform.sourceInfoWithColumns', {
        name: source.nodeName,
        count: columnCount,
      });
    }
    return t('nodes.config.transform.sourceInfoConnected', { name: source.nodeName });
  }, [inputColumns, availableColumns.length, t]);

  const updateRules = (newRules: TransformRule[]) => {
    const node = store.nodes.find(n => n.id === nodeId);
    if (node) {
      store.updateNode(nodeId, {
        ...node,
        data: {
          ...node.data,
          config: {
            ...node.data.config,
            params: {
              ...node.data.config.params,
              rules: newRules,
            },
          },
        },
      });
    }

    // 根据规则推导当前节点的输出列，并写入 SchemaContext
    if (!upstreamColumns.length || !node) return;

    // 从上游列复制一份作为基础；继承上游动态字段组
    const nextColumns = [...upstreamColumns];
    const nextDynamicGroups: DynamicGroup[] = [
      ...(getNodeSchema(upstreamNodeId ?? '')?.dynamicGroups ?? []),
    ];

    newRules.forEach(rule => {
      if (!rule.enabled) return;

      // 交换列：不添加新列，列名保持不变
      if (rule.type === 'swap_columns') {
        // 交换列只是交换数据，不改变列结构
        return;
      }

      const rawTarget = rule.targetColumn?.trim();
      const targetName =
        rawTarget ||
        (rule.type === 'merge_columns'
          ? ''
          : rule.type === 'copy_column'
            ? `${rule.sourceColumn}_copy`
            : rule.sourceColumn);
      if (!targetName) return;

      const multiValueMode = (rule.params?.multiValueMode as string) || 'merge';
      const maxSuffix = Math.min(100, Math.max(1, Number(rule.params?.maxSuffixColumns) || 10));

      // 提取数字 + 分列：输出为 目标_1, 目标_2, ...，并登记动态字段组
      if (rule.type === 'extract_number' && multiValueMode === 'suffix') {
        const physicalCols: string[] = [];
        for (let i = 1; i <= maxSuffix; i++) {
          const colName = `${targetName}_${i}`;
          if (nextColumns.some(c => c.name === colName)) continue;
          const sourceCol = nextColumns.find(c => c.name === rule.sourceColumn) ||
            upstreamColumns.find(c => c.name === rule.sourceColumn);
          nextColumns.push({
            name: colName,
            type: sourceCol?.type ?? 'string',
            nullable: true,
            index: nextColumns.length,
            dynamicGroup: targetName,
          });
          physicalCols.push(colName);
        }
        if (physicalCols.length > 0) {
          nextDynamicGroups.push({ commonName: targetName, physicalColumns: physicalCols });
        }
        return;
      }

      // 分割为多列：前端预览用多列 + 动态字段组（列数预览用固定上限，实际由执行结果决定）
      if (rule.type === 'split_to_columns') {
        const maxPreview = 10;
        const physicalCols: string[] = [];
        for (let i = 1; i <= maxPreview; i++) {
          const colName = `${targetName}_${i}`;
          if (nextColumns.some(c => c.name === colName)) continue;
          const sourceCol = nextColumns.find(c => c.name === rule.sourceColumn) ||
            upstreamColumns.find(c => c.name === rule.sourceColumn);
          nextColumns.push({
            name: colName,
            type: sourceCol?.type ?? 'string',
            nullable: true,
            index: nextColumns.length,
            dynamicGroup: targetName,
          });
          physicalCols.push(colName);
        }
        if (physicalCols.length > 0) {
          nextDynamicGroups.push({ commonName: targetName, physicalColumns: physicalCols });
        }
        return;
      }

      if (nextColumns.some(c => c.name === targetName)) return;

      let sourceCol = null;
      if (rule.type === 'merge_columns' && rule.sourceColumns && rule.sourceColumns.length > 0) {
        sourceCol = nextColumns.find(c => c.name === rule.sourceColumns![0]) ||
                   upstreamColumns.find(c => c.name === rule.sourceColumns![0]);
      } else {
        sourceCol = nextColumns.find(c => c.name === rule.sourceColumn) ||
                   upstreamColumns.find(c => c.name === rule.sourceColumn);
      }

      nextColumns.push({
        name: targetName,
        type: sourceCol?.type ?? 'string',
        nullable: true,
        index: nextColumns.length,
      });
    });

    register({
      nodeType: node.type,
      nodeName: getNodeDisplayLabel(node.type, node.data.label, t, i18n),
      columns: nextColumns,
      dynamicGroups: nextDynamicGroups.length > 0 ? nextDynamicGroups : undefined,
    });
  };

  const addRule = () => {
    const newRule: TransformRule = {
      id: generateId(),
      sourceColumn: availableColumns[0] || '',
      targetColumn: '',
      type: 'text_case',
      params: getDefaultParams('text_case'),
      enabled: true,
    };
    updateRules([...rules, newRule]);
  };

  const updateRule = (index: number, updates: Partial<TransformRule>) => {
    const newRules = rules.map((rule, i) => 
      i === index ? { ...rule, ...updates } : rule
    );
    updateRules(newRules);
  };

  const removeRule = (index: number) => {
    const newRules = rules.filter((_, i) => i !== index);
    updateRules(newRules);
  };

  return (
    <div className="space-y-4">
      {/* Source Info */}
      {isConnected ? (
        hasRealColumns ? (
          <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 rounded-lg border border-emerald-100">
            <ArrowsClockwise className="w-4 h-4 text-emerald-600" />
            <span className="text-xs text-emerald-700">
              {t('nodes.config.transform.sourceLabel')}: {sourceInfo}
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg border border-blue-100">
            <ArrowsClockwise className="w-4 h-4 text-blue-600" />
            <span className="text-xs text-blue-700">
              {t('nodes.config.transform.connectedNoColumns', { sourceInfo })}
            </span>
          </div>
        )
      ) : (
        <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 rounded-lg border border-amber-100">
          <ArrowsClockwise className="w-4 h-4 text-amber-600" />
          <span className="text-xs text-amber-700">
            {t('nodes.config.transform.notConnected')}
          </span>
        </div>
      )}

      {/* Rules List */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-xs font-semibold text-zinc-700">
            {t('nodes.config.transform.transformations')} ({rules.length})
          </label>
          <Button
            variant="secondary"
            size="sm"
            onClick={addRule}
            disabled={!isConnected}
            leftIcon={<Plus className="w-3.5 h-3.5" />}
          >
            {t('nodes.config.transform.addRule')}
          </Button>
        </div>

        {rules.length === 0 ? (
          <div className="text-center py-8 bg-zinc-50 rounded-xl border border-zinc-200 border-dashed">
            <ArrowsClockwise className="w-10 h-10 text-zinc-300 mx-auto mb-2" />
            <p className="text-sm text-zinc-500">暂无转换规则</p>
            <p className="text-xs text-zinc-400 mt-1">
              {isConnected 
                ? '点击上方按钮添加数据转换规则'
                : '请先连接输入节点'
              }
            </p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1">
            {rules.map((rule, index) => (
              <RuleCard
                key={rule.id}
                rule={rule}
                index={index}
                availableColumns={availableColumns}
                isConnected={isConnected}
                hasRealColumns={hasRealColumns}
                onUpdate={(updates) => updateRule(index, updates)}
                onRemove={() => removeRule(index)}
                t={t}
              />
            ))}
          </div>
        )}
      </div>

      {/* Help Text */}
      <div className="text-xs text-zinc-500 bg-zinc-50 p-3 rounded-lg border border-zinc-100">
        <p className="font-medium mb-1.5">
          {t('nodes.config.transform.usageTitle')}
        </p>
        <ul className="space-y-1 text-zinc-400">
          <li className="flex items-start gap-1.5">
            <span className="text-emerald-500">•</span>
            <span>{t('nodes.config.transform.usageSource')}</span>
          </li>
          <li className="flex items-start gap-1.5">
            <span className="text-emerald-500">•</span>
            <span>{t('nodes.config.transform.usageTarget')}</span>
          </li>
          <li className="flex items-start gap-1.5">
            <span className="text-emerald-500">•</span>
            <span>{t('nodes.config.transform.usageOrder')}</span>
          </li>
        </ul>
      </div>
    </div>
  );
}
